import React, { useContext, useState, useRef } from 'react';
import { AppContext } from '../../store/contexts/AppContext.js';
import classNames from 'classnames';
import _find from 'lodash/find';
import _noop from 'lodash/noop';
import propTypes from 'prop-types';
import { AnimatedTextHeader } from '_ui-core-js/React/components/AnimatedTextHeader.jsx';
import { Button } from '_ui-core-js/React/components/Button.jsx';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { LoginBanner, StickyHeroButton, StickyHeroButtonAnchor } from '../index';
import { VideoPlayer } from '_ui-core-js/React/components/VideoPlayer.jsx';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';

export function VideoHeroBanner(props) {
    const {cmsConfiguration} = useContext(AppContext);
    const contentSettings = _.get(cmsConfiguration, `settings`, {});

    const { title, subtitle, backgroundImage, heading, copy, links, stickyLink, settings, onClickActions, loginBanner, video, disableFullscreenDefault, enableLargeHeading } = props;
    const [hideHeading, setHideHeading] = useState(false);
    const [showVideoFullscreen, setShowVideoFullScreen] = useState(!disableFullscreenDefault);
    const heroAnchorRef = useRef();

    const block = 'c-video-hero-banner';
    const styles =  `.${block}::before {
        background-image: url(${backgroundImage});
    }`;

    function handleOnClick (link) {
        if (_.find(link.dataAttributes, {key:'onClickAction'})) {
            const onClickAction = _.find(link.dataAttributes, {key:'onClickAction'})
            let onClickFunction = _.noop();
        
            if (onClickAction?.value) {
                onClickFunction = onClickActions[onClickAction.value]
            }
            onClickFunction(link);
        }
	}

    function renderButtons() {

		const markup = _.isEmpty(links) ? null : 
            <div className={`${block}__ctas`}>
				{ links.map((link) => 
					<div className={`${block}__button`} key={`${block}-${link.id}`}>
						<Button {...{
							id: `${link.id}`,
							label : link.label,
							mobileLabel: link.mobileLabel,
                            gtmLabel : CORE_GTM_TRACKING_VALUES.label[link.id] || '',
                            gtmComponentName : CORE_GTM_TRACKING_VALUES.componentName.videoBanner,
                            gtmComponentGroup : CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
							testId : `${link.id}`,
							type: link.type.toLowerCase(),
                            cssAnimation: false,
							onClickCallback : () => {
								handleOnClick(link)
							}, 
						}}/>
					</div>
				)}
			</div>
		
		return markup
	}

    return (
        <>
            <style>{styles}</style>
            <article 
                className={`${block} ${contentSettings.enableLbxLaunchUpdates ? block + '__launch' : ''} ${enableLargeHeading ?'large-heading' : ''}`}
                aria-label={subtitle + ' ' + title} 
            >
                <div className='container-xxl'>
                    {
                        !contentSettings.enableLbxLaunchUpdates &&
                            <LoginBanner {...{
                                loginBanner,
                                onClickActions
                            }}/>
                    }
                          
                    <AnimatedTextHeader
                        heading={title}
                        HeadingTag='h1'
                        blockClasses={
                            classNames({
                                [`c-animated-text-header--hidden`]: hideHeading
                            })
                        }
                        headingClasses={`${block}__main-heading-text`}
                        subheading={subtitle}
                        subheadingExpandedWidth={true}
                        cssAnimationTrigger={!hideHeading}
                    />
                    
                    <div className={`${block}__video-container dark-indicator`}>
                        <VideoPlayer
                            src={video.url}
                            assetRootPath={`${settings.assetRootPath}/assets/images/`}
                            poster={video.poster}
                            fullscreen={showVideoFullscreen}
                            label={video.label}
                            onPlay={(isVideoPlayingFullscreen) => {
                                setShowVideoFullScreen(isVideoPlayingFullscreen),
                                setHideHeading(true)
                            }}
                            onEnd={() => {
                                setHideHeading(false)
                            }}
                            onPause={(isVideoPlayingFullscreen) => {
                                setShowVideoFullScreen(isVideoPlayingFullscreen);
                                setHideHeading(false)
                            }}
                            gtmComponentName={CORE_GTM_TRACKING_VALUES.componentName.videoBanner}
                            gtmComponentGroup={CORE_GTM_TRACKING_VALUES.componentGroup.newCars}
                        />
                        {
                            contentSettings.enableLbxLaunchUpdates &&
                                <StickyHeroButtonAnchor 
                                  ref={heroAnchorRef}
                                />
                        }
                    </div>

                    {
                        contentSettings.enableLbxLaunchUpdates &&
                            <StickyHeroButton
                                {...stickyLink}
                                anchorRef={heroAnchorRef}
                                onClickCallback={handleOnClick}
                            />
                    }

                    {
                        !contentSettings.enableLbxLaunchUpdates &&
                            <CssAnimation
                                enter={{
                                    name: 'short-slide-up-fade-in',
                                    delay: '0.5, hidden',
                                    duration: 1
                                }}
                                triggerEnter={'in-view-init'}
                            >
                                <section className={`${block}__content-grid container-xl`}>
                                    <header className={`${block}__header`}>
                                        <h2 className={`${block}__heading`}>
                                            {heading}
                                        </h2>
                                    </header>
                                    <div>
                                        <div className={`${block}__grid__content`}>
                                            <p>{copy}</p>
                                            {renderButtons()}
                                        </div>
                                    </div>
                                </section>
                    
                            </CssAnimation>
                    }
                </div>
            </article>
        </> 
    );
}

VideoHeroBanner.propTypes = {
    title:propTypes.string, 
    subtitle:propTypes.string, 
    backgroundImage:propTypes.string, 
    heading:propTypes.string, 
    copy:propTypes.string, 
    links:propTypes.array, 
    settings:propTypes.object, 
    onClickActions:propTypes.object, 
    loginBanner:propTypes.object, 
    video:propTypes.object
};

